import type { EconomyConfig } from '../config/types';

export interface EconomyFns {
  milestoneMult: (level: number) => number;
  siteAdvantage: (site: number, key: string, defaultValue?: number) => number;
  softCap: (raw: number, cap: number, power?: number) => number;
  shaftProd: (shaft: number, level: number, site?: number) => number;
  shaftLevelCost: (shaft: number, level: number, site?: number) => number;
  shaftUnlockCost: (shaft: number, site?: number) => number;
  shaftManagerCost: (shaft: number, site?: number) => number;
  bulkCost: (shaft: number, level: number, count: number, site?: number) => number;
  maxAffordable: (shaft: number, level: number, gold: number, site?: number) => number;
  elevatorCap: (level: number) => number;
  warehouseCap: (level: number) => number;
  haulFactor: (siteIndex: number, deepestUnlockedShaft: number) => number;
  elevatorCost: (level: number, siteIndex: number, deepestUnlockedShaft: number) => number;
  warehouseCost: (level: number, siteIndex: number, deepestUnlockedShaft: number) => number;
  siteValueMult: (siteIndex: number) => number;
  cycleSeconds: (siteIndex?: number) => number;
  glowstones: (deepestUnlockedShaft: number) => number;
  config: EconomyConfig;
}

/**
 * Soft-cap: smooth min(raw, cap) = raw / (1 + (raw/cap)^p)^(1/p).
 * Mirror of tools/economy_sim.py `_soft_cap`. cap<=0 disables.
 */
export function softCap(raw: number, cap: number, power = 2): number {
  if (!cap || cap <= 0 || raw <= 0) return raw;
  const p = Math.max(1 / 2, power);
  return raw / (1 + (raw / cap) ** p) ** (1 / p);
}

/** Pure economy formulas bound to an economy config object. */
export function createEconomy(e: EconomyConfig): EconomyFns {
  const S = e.shafts;
  const M = e.managers;
  const elev = e.elevator;
  const wh = e.warehouse;
  const mig = e.migration;
  const ts = e.transportScaling;

  function milestoneMult(level: number): number {
    let m = 1;
    for (const t of e.milestones.tiers) {
      if (level >= t.level) m *= t.multiplier;
    }
    return m;
  }

  function siteAdvantage(site: number, key: string, defaultValue = 1): number {
    const adv = mig.siteAdvantages;
    if (!adv) return defaultValue;
    const arr = adv[key as keyof typeof adv];
    if (!arr || !Array.isArray(arr) || arr.length === 0) return defaultValue;
    return arr[Math.min(site, arr.length - 1)] ?? defaultValue;
  }

  function shaftProd(shaft: number, level: number, site = 0): number {
    if (level <= 0) return 0;
    let raw = S.baseProd * S.prodGrowth ** (shaft - 1) * level * milestoneMult(level);
    raw = softCap(raw, S.prodSoftCap ?? 0, S.prodSoftCapPower ?? 2);
    return raw * siteAdvantage(site, 'digSpeedMult', 1);
  }

  function shaftLevelCost(shaft: number, level: number, site = 0): number {
    const raw =
      S.baseLevelCost *
      S.levelCostGrowth ** (shaft - 1) *
      S.levelCostRate ** (level - 1) *
      siteAdvantage(site, 'levelCostMult', 1);
    return softCap(raw, S.levelCostSoftCap ?? 0, S.levelCostSoftCapPower ?? 2);
  }

  function shaftUnlockCost(shaft: number, site = 0): number {
    if (shaft <= 1) return 0;
    const raw =
      S.unlockBase *
      S.unlockGrowth ** (shaft - 2) *
      siteAdvantage(site, 'unlockCostMult', 1);
    return softCap(raw, S.unlockSoftCap ?? 0, S.unlockSoftCapPower ?? 2);
  }

  function shaftManagerCost(shaft: number, site = 0): number {
    return (
      M.shaftManagerCostFactor *
      Math.max(shaftUnlockCost(shaft, site), M.shaftManagerMinCost)
    );
  }

  /** Sum of per-level costs (soft-cap prevents closed-form geometric). */
  function bulkCost(shaft: number, level: number, count: number, site = 0): number {
    if (count <= 0) return 0;
    let sum = 0;
    for (let k = 0; k < count; k++) {
      sum += shaftLevelCost(shaft, level + k, site);
    }
    return sum;
  }

  function maxAffordable(
    shaft: number,
    level: number,
    gold: number,
    site = 0,
  ): number {
    const maxBuy = Math.max(0, S.maxLevel - level);
    if (maxBuy === 0 || gold <= 0) return 0;
    const c0 = shaftLevelCost(shaft, level, site);
    if (gold < c0 || c0 <= 0) return 0;
    // Binary search: soft-cap breaks the geometric closed-form log estimate.
    let lo = 0;
    let hi = maxBuy;
    while (lo < hi) {
      const mid = Math.floor((lo + hi + 1) / 2);
      if (bulkCost(shaft, level, mid, site) <= gold) lo = mid;
      else hi = mid - 1;
    }
    return lo;
  }

  function elevatorCap(level: number): number {
    const raw = elev.baseCap * elev.capGrowth ** (level - 1);
    return softCap(raw, ts.capSoftCap ?? 0, ts.capSoftCapPower ?? 2);
  }

  function warehouseCap(level: number): number {
    const raw = wh.baseCap * wh.capGrowth ** (level - 1);
    return softCap(raw, ts.capSoftCap ?? 0, ts.capSoftCapPower ?? 2);
  }

  function siteValueMult(siteIndex: number): number {
    return mig.siteValueGrowth ** siteIndex;
  }

  /**
   * haulFactor = siteValue^siteExp × transportCostMult × depthGrowth^(depth-1).
   * v3: siteExp=0; transportCostMult from migration.siteAdvantages.
   */
  function haulFactor(siteIndex: number, deepestUnlockedShaft: number): number {
    return (
      siteValueMult(siteIndex) ** (ts.siteExp ?? 0) *
      siteAdvantage(siteIndex, 'transportCostMult', 1) *
      ts.depthGrowth ** (deepestUnlockedShaft - 1)
    );
  }

  function elevatorCost(
    level: number,
    siteIndex: number,
    deepestUnlockedShaft: number,
  ): number {
    const raw =
      elev.baseCost *
      elev.costRate ** (level - 1) *
      haulFactor(siteIndex, deepestUnlockedShaft);
    return softCap(raw, ts.costSoftCap ?? 0, ts.costSoftCapPower ?? 2);
  }

  function warehouseCost(
    level: number,
    siteIndex: number,
    deepestUnlockedShaft: number,
  ): number {
    const raw =
      wh.baseCost *
      wh.costRate ** (level - 1) *
      haulFactor(siteIndex, deepestUnlockedShaft);
    return softCap(raw, ts.costSoftCap ?? 0, ts.costSoftCapPower ?? 2);
  }

  function cycleSeconds(siteIndex = 0): number {
    return S.cycleSeconds * siteAdvantage(siteIndex, 'cycleSecondsMult', 1);
  }

  function glowstones(deepestUnlockedShaft: number): number {
    return Math.max(
      0,
      mig.glowstonesPerDepth * (deepestUnlockedShaft - mig.glowstoneDepthOffset),
    );
  }

  return {
    milestoneMult,
    siteAdvantage,
    softCap,
    shaftProd,
    shaftLevelCost,
    shaftUnlockCost,
    shaftManagerCost,
    bulkCost,
    maxAffordable,
    elevatorCap,
    warehouseCap,
    haulFactor,
    elevatorCost,
    warehouseCost,
    siteValueMult,
    cycleSeconds,
    glowstones,
    config: e,
  };
}
